// PlatformSecrets — super_admin-only secrets management UI (PR C).
//
// 使用一般使用者語言：
//   - "金鑰" 不是 "secret/token"
//   - "已設定 / 尚未設定" 而非 "set/unset"
//   - "舊系統設定" 而非 "env_fallback"
//   - 操作前都先確認

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  KeyRound,
  RefreshCw,
  Eye,
  EyeOff,
  Trash2,
  ServerCrash,
  CheckCircle2,
  AlertCircle,
  Pencil,
  Inbox,
} from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import EmptyState from "../../components/EmptyState.js";
import Skeleton from "../../components/Skeleton.js";
import Drawer from "../../components/Drawer.js";
import { Field } from "../../components/Field.js";
import {
  listPlatformSecrets,
  setPlatformSecret,
  deletePlatformSecret,
  friendlyPlatformSecretsError,
  type SecretMeta,
} from "../../lib/platformSecretsApi.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; items: SecretMeta[] }
  | { status: "error"; message: string };

type DrawerState = null | { mode: "edit"; secret: SecretMeta };
type ConfirmState = null | { kind: "delete"; secret: SecretMeta };

const STATUS_BADGE: Record<SecretMeta["status"] | "env_fallback", { label: string; className: string }> = {
  set: { label: "已設定", className: "badge-success" },
  rotating: { label: "輪替中", className: "badge-warning" },
  unset: { label: "尚未設定", className: "badge-default" },
  env_fallback: { label: "舊系統設定", className: "badge-warning" },
};

export default function PlatformSecrets(): React.ReactElement {
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<DrawerState>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listPlatformSecrets();
      setState({ status: "ready", items: r.items });
    } catch (err) {
      setState({ status: "error", message: friendlyPlatformSecretsError(err) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    if (state.status !== "ready") return new Map<string, SecretMeta[]>();
    const m = new Map<string, SecretMeta[]>();
    for (const it of state.items) {
      const k = it.category_label;
      const list = m.get(k) ?? [];
      list.push(it);
      m.set(k, list);
    }
    return m;
  }, [state]);

  const onDelete = async (secret: SecretMeta) => {
    setConfirm(null);
    try {
      await deletePlatformSecret(secret.key);
      setToast(`已刪除「${secret.key}」`);
      void load();
    } catch (err) {
      setToast(friendlyPlatformSecretsError(err));
    }
  };

  return (
    <>
      <PageHeader
        title="平台金鑰"
        description="集中管理 Resend、OAuth、LINE 等服務金鑰。所有設定都加密保存於資料庫，並會留下操作紀錄。"
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
            <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />
      {toast && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 p-3 text-sm text-success">
          <CheckCircle2 className="h-4 w-4" />
          <span className="flex-1">{toast}</span>
          <button className="text-2xs underline" onClick={() => setToast(null)}>關閉</button>
        </div>
      )}
      {state.status === "loading" && <SecretsSkeleton />}
      {state.status === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="金鑰列表載入失敗"
          description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>}
        />
      )}
      {state.status === "ready" && state.items.length === 0 && (
        <EmptyState
          icon={Inbox}
          title="目前沒有任何金鑰項目"
          description="此專案尚未設定任何已知金鑰。請聯絡技術支援。"
        />
      )}
      {state.status === "ready" && state.items.length > 0 && (
        <div className="space-y-4">
          {[...grouped.entries()].map(([cat, items]) => (
            <CategoryCard key={cat} title={cat} items={items} onEdit={(s) => setEditing({ mode: "edit", secret: s })} onDelete={(s) => setConfirm({ kind: "delete", secret: s })} />
          ))}
        </div>
      )}

      <Drawer
        open={editing?.mode === "edit"}
        onClose={() => setEditing(null)}
        title={editing ? `設定「${editing.secret.key}」` : "設定金鑰"}
        description="儲存後立即生效，並寫入操作紀錄。系統不會在任何頁面顯示原始值。"
        size="lg"
      >
        {editing?.mode === "edit" && (
          <SecretForm
            secret={editing.secret}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setToast(`「${editing.secret.key}」已更新並立即生效`);
              void load();
            }}
          />
        )}
      </Drawer>

      {confirm?.kind === "delete" && (
        <ConfirmDelete
          secret={confirm.secret}
          onCancel={() => setConfirm(null)}
          onConfirm={() => void onDelete(confirm.secret)}
        />
      )}
    </>
  );
}

function CategoryCard({ title, items, onEdit, onDelete }: {
  title: string;
  items: SecretMeta[];
  onEdit: (s: SecretMeta) => void;
  onDelete: (s: SecretMeta) => void;
}): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-border px-4 py-3 text-sm font-semibold text-text-primary">{title}</div>
      <ul className="divide-y divide-border">
        {items.map((s) => (
          <li key={s.key} className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm text-text-primary truncate">{s.key}</span>
                <StatusBadge meta={s} />
                {s.required && <span className="badge-warning">必填</span>}
              </div>
              <p className="mt-1 text-xs text-text-secondary leading-snug">{s.description ?? "—"}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-2xs text-text-muted">
                {s.display_hint && <span className="font-mono">···{s.display_hint}</span>}
                {s.updated_at && <span>更新於 {formatTime(s.updated_at)}</span>}
                {s.source === "env_fallback" && <span className="text-warning">建議搬移：目前使用環境變數，請改在這裡設定</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:justify-end">
              <button className="btn-secondary text-xs" onClick={() => onEdit(s)}>
                <Pencil className="h-3.5 w-3.5" /> {s.status === "set" ? "更新" : "設定"}
              </button>
              {s.status === "set" && (
                <button className="btn-secondary text-xs text-danger" onClick={() => onDelete(s)}>
                  <Trash2 className="h-3.5 w-3.5" /> 刪除
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusBadge({ meta }: { meta: SecretMeta }): React.ReactElement {
  if (meta.source === "env_fallback") {
    const def = STATUS_BADGE.env_fallback;
    return <span className={def.className}>{def.label}</span>;
  }
  const def = STATUS_BADGE[meta.status];
  return <span className={def.className}>{def.label}</span>;
}

function formatTime(unixSec: number): string {
  return formatTaipeiDateTime(unixSec * 1000);
}

function SecretForm({ secret, onCancel, onSaved }: {
  secret: SecretMeta;
  onCancel: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const [value, setValue] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const inputType = reveal ? "text" : "password";

  const submit = async () => {
    if (!value) {
      setErr("請輸入金鑰值再儲存。");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await setPlatformSecret(secret.key, value);
      onSaved();
    } catch (e) {
      setErr(friendlyPlatformSecretsError(e));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded border border-border bg-bg-raised p-3">
        <p className="text-xs text-text-secondary">金鑰名稱</p>
        <p className="mt-0.5 font-mono text-sm text-text-primary">{secret.key}</p>
        {secret.description && (
          <p className="mt-2 text-xs text-text-secondary leading-snug">{secret.description}</p>
        )}
      </div>

      <Field label="金鑰值" required hint="儲存後立即生效。系統會以加密方式保存，不會在任何頁面顯示原始值。">
        {secret.multiline ? (
          <textarea
            className="textarea font-mono"
            rows={8}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="貼上完整內容（包含 BEGIN/END 行如有）"
          />
        ) : (
          <div className="relative">
            <input
              className="input font-mono pr-10"
              type={inputType}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="輸入新的金鑰值"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-text-muted hover:text-text-primary"
              aria-label={reveal ? "隱藏" : "顯示"}
            >
              {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        )}
      </Field>

      {err && (
        <div className="flex items-start gap-2 rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{err}</span>
        </div>
      )}

      {!confirming ? (
        <div className="flex gap-2 sm:justify-end">
          <button onClick={onCancel} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button onClick={() => setConfirming(true)} className="btn-primary text-sm" disabled={busy || !value}>
            下一步
          </button>
        </div>
      ) : (
        <div className="rounded border border-warning/40 bg-warning/10 p-4 space-y-3">
          <p className="text-sm text-text-primary">
            確定要更新「<span className="font-mono">{secret.key}</span>」嗎？儲存後會立即生效，並寫入操作紀錄。
          </p>
          <div className="flex gap-2 sm:justify-end">
            <button onClick={() => setConfirming(false)} className="btn-secondary text-sm" disabled={busy}>返回修改</button>
            <button onClick={() => void submit()} className="btn-primary text-sm" disabled={busy}>{busy ? "儲存中…" : "確認儲存"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmDelete({ secret, onCancel, onConfirm }: {
  secret: SecretMeta;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  const [stage, setStage] = useState<1 | 2>(1);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-bg-surface p-6 shadow-2xl">
        <h2 className="text-base font-semibold text-text-primary">刪除金鑰</h2>
        <p className="mt-2 text-sm text-text-secondary">
          您即將刪除「<span className="font-mono text-text-primary">{secret.key}</span>」。
          此金鑰被刪除後，依賴它的功能（{secret.description ?? "相關服務"}）將立即無法運作，
          直到您再次設定為止。
        </p>
        {stage === 1 ? (
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={onCancel} className="btn-secondary text-sm">取消</button>
            <button onClick={() => setStage(2)} className="btn-danger text-sm">繼續刪除</button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-text-primary">最後確認：真的要刪除嗎？</p>
            <div className="flex justify-end gap-2">
              <button onClick={onCancel} className="btn-secondary text-sm">不要刪了</button>
              <button onClick={onConfirm} className="btn-danger text-sm">確認刪除</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SecretsSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card p-4 space-y-3">
          <Skeleton className="h-5 w-32" />
          {[0, 1, 2].map((j) => (
            <div key={j} className="flex items-start gap-4">
              <KeyRound className="h-4 w-4 text-text-muted flex-shrink-0 mt-1" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <Skeleton className="h-7 w-16" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
